import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { outboundCallAtom } from '@/state/atom/voiceCallAtom';
import { voiceStatusAtom } from '@/state/atom/voiceCallAtom';
import Constant from '@/common/constant/Constant';
import { useSnackbar } from 'notistack';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';
// Removed unused imports: projectInuseDetailAtom, corporationAtom, userAtom, saveRecentOutboundCall
// Recent call saving is now handled by backend webhook (twilioWebhookRequestVoiceOutbound)

/**
 * Detect CALL_ALREADY_IN_PROGRESS error from Twilio SDK
 * @param {Error} error - Error thrown by Twilio device.connect()
 * @returns {boolean} true if error indicates a concurrent call rejection
 */
const isConcurrentCallError = (error) => {
    const errorMsg = error?.message || '';
    const errorBody = error?.body || '';
    return errorMsg.includes(Constant.CALL_ALREADY_IN_PROGRESS)
        || errorBody.includes(Constant.CALL_ALREADY_IN_PROGRESS);
};

/**
 * Hook for managing outbound call state and operations
 */
export const useOutboundCall = () => {
    const [outboundCall, setOutboundCall] = useRecoilState(outboundCallAtom);
    const [voiceStatus, setVoiceStatus] = useRecoilState(voiceStatusAtom);
    const { enqueueSnackbar } = useSnackbar();
    // projectInuseDetail, corporationDetail, userDetail - removed as saving is now handled by backend webhook

    /**
     * Show snackbar error based on call result
     * @param {Object} result - Result from initiateCall
     */
    const showCallError = (result) => {
        if (!result || result.success) return;
        if (result.errorCode === Constant.CALL_ALREADY_IN_PROGRESS) {
            enqueueSnackbar(buildSnackbarMessage('MSG_CALL_ALREADY_IN_PROGRESS'), {
                variant: 'error',
                autoHideDuration: 3000
            });
        } else if (result.error) {
            enqueueSnackbar(buildSnackbarMessage('MSG_CALL_FAILED'), {
                variant: 'error',
                autoHideDuration: 3000
            });
        }
    };

    const initiateCall = async (phoneData) => {
        if (!window.twilioDevice) {
            console.error('[Outbound Call] Twilio device not initialized');
            return { success: false, error: 'DEVICE_NOT_INITIALIZED' };
        }

        setVoiceStatus(Constant.PHONE_AVAILABILITY.CONNECTING);
        const deviceState = window.twilioDevice.state;
        if (deviceState !== 'registered' && voiceStatus !== Constant.PHONE_AVAILABILITY.READY) {
            console.warn('[Outbound Call] Device not ready:', { deviceState, voiceStatus });
            return { success: false, error: 'DEVICE_NOT_READY' };
        }

        try {
            const device = window.twilioDevice;
            const connection = await device.connect({
                params: {
                    To: phoneData.phone,
                    ticketId: phoneData.ticketId || '',
                    customerName: phoneData.name || '',
                    sourcePanel: phoneData.sourcePanel || 'TICKET_LIST',
                    callType: phoneData.callType || '',
                    callerProjectId: phoneData.callerProjectId || phoneData.projectId || '',
                    calleeProjectId: phoneData.calleeProjectId || ''
                }
            });
            window.twilioOutboundConnection = connection;

            const callSid = connection.parameters?.CallSid || '';

            const outboundCallData = {
                phoneNumber: phoneData.phone,
                customerName: phoneData.name || '',
                callSid: callSid,
                timestamp: Date.now()
            };
            
            setOutboundCall(outboundCallData);

            setupConnectionHandlers(
                connection, 
                setOutboundCall, 
                setVoiceStatus,
                enqueueSnackbar,
                { ticketId: phoneData.ticketId || '' }
            );

            return { success: true, connection };
        } catch (error) {
            console.error('[Outbound Call] Exception:', error);
            setVoiceStatus(Constant.PHONE_AVAILABILITY.READY);
            const result = isConcurrentCallError(error)
                ? { success: false, errorCode: Constant.CALL_ALREADY_IN_PROGRESS }
                : { success: false, error: error.message };
            showCallError(result);
            return result;
        }
    };

    const cancelCall = useCallback(async () => {
        if (!outboundCall) return;

        try {
            if (window.twilioOutboundConnection) {
                window.twilioOutboundConnection.disconnect();
                delete window.twilioOutboundConnection;
            }
            setOutboundCall(null);
            setVoiceStatus(Constant.PHONE_AVAILABILITY.READY);
        } catch (error) {
            setOutboundCall(null);
            setVoiceStatus(Constant.PHONE_AVAILABILITY.READY);
        }
    }, [outboundCall, setOutboundCall, setVoiceStatus]);

    return {
        outboundCall,
        voiceStatus,
        initiateCall,
        cancelCall
    };
};

/**
 * Setup event handlers for Twilio connection.
 *
 * Conflict detection strategy:
 *   - When backend returns TwiML <Reject reason="busy">, Twilio SDK fires error event
 *     with code 31486 (BusyHere). This is the definitive signal for concurrent call block.
 *   - The ringing event fires BEFORE the webhook responds, so it cannot be used to
 *     distinguish callee-reject from webhook-reject.
 *
 * Cases:
 *   1. Agent hangs up           → _shouldSendHangup=true                        → silent
 *   2. Callee reject/busy       → _shouldSendHangup=false, _mediaStatus="open"  → silent
 *   3. Callee reject (no answer)→ error 31486 NOT fired                         → silent
 *   4. Concurrent call blocked  → error 31486 (BusyHere) fired                  → MSG_CALL_ALREADY_IN_PROGRESS
 *
 * TODO(twilio-upgrade): Re-verify error codes whenever Twilio Voice SDK version is upgraded.
 */
const setupConnectionHandlers = (
    connection,
    setOutboundCall,
    setVoiceStatus,
    enqueueSnackbar,
    options = {}
) => {
    // Track whether the call was answered (media connected).
    let hasBeenAnswered = false;
    // Prevent showing the concurrent-call toast more than once
    // (Twilio SDK may fire both error + disconnect for the same rejection).
    let hasShownConflictToast = false;

    const showConflictToastOnce = () => {
        if (hasShownConflictToast) return;
        hasShownConflictToast = true;
        enqueueSnackbar(buildSnackbarMessage('MSG_CALL_ALREADY_IN_PROGRESS'), {
            variant: 'error',
            autoHideDuration: 3000
        });
    };

    connection.on('accept', () => {
        hasBeenAnswered = true;
        setVoiceStatus(Constant.PHONE_AVAILABILITY.ACTIVE);
        // Update accepted timestamp when call is accepted (user picks up)
        // Also update callSid if not available at connect time (Twilio SDK assigns after ringing)
        setOutboundCall(prev => {
            if (prev) {
                return {
                    ...prev,
                    acceptedTimestamp: Date.now(),
                    callSid: prev.callSid || connection.parameters?.CallSid || '',
                };
            }
            return prev;
        });
    });

    connection.on('disconnect', (data) => {
        setOutboundCall(null);
        setVoiceStatus(Constant.PHONE_AVAILABILITY.READY);

        // Agent initiated hangup → silent
        if (data?._shouldSendHangup) return;

        // Media was connected (call was answered then ended normally) → silent
        if (data?._mediaStatus === 'open' || hasBeenAnswered) return;

        // For disconnect with _mediaStatus="pending":
        // - If error 31486 already fired → toast was shown by error handler
        // - If no error fired → callee rejected/timed out normally → silent
        // No action needed here; conflict toast is handled by error event.
    });

    connection.on('cancel', () => {
        setOutboundCall(null);
        setVoiceStatus(Constant.PHONE_AVAILABILITY.READY);
    });

    connection.on('reject', () => {
        setOutboundCall(null);
        setVoiceStatus(Constant.PHONE_AVAILABILITY.READY);
    });

    connection.on('error', (error) => {
        console.error('[Outbound Call] Error:', { code: error.code, message: error.message });
        setOutboundCall(null);
        setVoiceStatus(Constant.PHONE_AVAILABILITY.READY);

        switch (error?.code) {
            // TwiML <Reject reason="rejected"> (SIP 603) — concurrent call on same ticket
            case 31603:
            case 31404:
                if (options?.ticketId) {
                    showConflictToastOnce();
                }
                break;
            // Callee device offline or no agents available
            case 31480:
                enqueueSnackbar(buildSnackbarMessage('TEL_INFO_NO_AVAILABLE_RECEIVER'), {
                    variant: 'info',
                    autoHideDuration: 3000
                });
                break;
            default:
                break;
        }
    });
};

export { setupConnectionHandlers };
